'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, Briefcase, ShoppingCart, FileText, TrendingUp, TrendingDown, ChevronRight, MapPin } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';

// ─── Shared data (mirrors JobsContent & JobDetailPanel) ───────────────────────

const jobsData = [
  { id: 'job-crm-9924', crmRef: 'JOB-CRM-9924', name: 'Merch drop — TechConf 2026', client: 'TechConf Events Ltd', receivedDate: '15/08/2026', deliveries: 4, pos: 4, sos: 4, totalPoValue: '£38,400', totalPoRaw: 38400, totalSoValue: '£46,080', totalSoRaw: 46080, status: 'active', description: 'Full branded merchandise drop for TechConf 2026: hoodies, tote bags, lanyards, and water bottles.' },
  { id: 'job-crm-9921', crmRef: 'JOB-CRM-9921', name: 'Branded hoodies — RetailCo Q3', client: 'RetailCo UK', receivedDate: '13/08/2026', deliveries: 2, pos: 2, sos: 2, totalPoValue: '£22,800', totalPoRaw: 22800, totalSoValue: '£27,360', totalSoRaw: 27360, status: 'active', description: 'Embroidered hoodies in three colourways for RetailCo Q3 staff uniform rollout.' },
  { id: 'job-crm-9919', crmRef: 'JOB-CRM-9919', name: 'Promo kit — SaaS Summit', client: 'SaaS Summit Ltd', receivedDate: '11/08/2026', deliveries: 6, pos: 6, sos: 6, totalPoValue: '£54,200', totalPoRaw: 54200, totalSoValue: '£65,040', totalSoRaw: 65040, status: 'active', description: 'Conference delegate promo kits including notebooks, pens, USB drives, and branded bags.' },
  { id: 'job-crm-9917', crmRef: 'JOB-CRM-9917', name: 'Tote bags — GreenFest', client: 'GreenFest CIC', receivedDate: '08/08/2026', deliveries: 3, pos: 3, sos: 3, totalPoValue: '£12,600', totalPoRaw: 12600, totalSoValue: '£15,120', totalSoRaw: 15120, status: 'active', description: 'Organic cotton tote bags with screen-printed logos for GreenFest sustainability event.' },
  { id: 'job-crm-9915', crmRef: 'JOB-CRM-9915', name: 'Embroidered polos — HealthOrg', client: 'HealthOrg NHS', receivedDate: '05/08/2026', deliveries: 5, pos: 5, sos: 5, totalPoValue: '£31,000', totalPoRaw: 31000, totalSoValue: '£37,200', totalSoRaw: 37200, status: 'active', description: 'NHS branded polo shirts for 5 regional health trusts, embroidered logo, 3 sizes.' },
  { id: 'job-crm-9912', crmRef: 'JOB-CRM-9912', name: 'Caps and beanies — SportsBrand', client: 'SportsBrand Ltd', receivedDate: '01/08/2026', deliveries: 3, pos: 3, sos: 3, totalPoValue: '£18,200', totalPoRaw: 18200, totalSoValue: '£21,840', totalSoRaw: 21840, status: 'active', description: 'Embroidered caps and beanies for SportsBrand winter launch campaign.' },
  { id: 'job-crm-9908', crmRef: 'JOB-CRM-9908', name: 'Desk kits — FinanceCorp', client: 'FinanceCorp PLC', receivedDate: '28/07/2026', deliveries: 2, pos: 2, sos: 2, totalPoValue: '£9,800', totalPoRaw: 9800, totalSoValue: '£11,760', totalSoRaw: 11760, status: 'complete', description: 'Premium desk kit bundles (notepad, pen set, cable organiser) for FinanceCorp new starters.' },
  { id: 'job-crm-9904', crmRef: 'JOB-CRM-9904', name: 'Branded water bottles — FitLife', client: 'FitLife Gyms', receivedDate: '22/07/2026', deliveries: 1, pos: 1, sos: 1, totalPoValue: '£7,200', totalPoRaw: 7200, totalSoValue: '£8,640', totalSoRaw: 8640, status: 'complete', description: 'Insulated stainless steel water bottles with laser-engraved logo for FitLife member welcome packs.' },
  { id: 'job-crm-9899', crmRef: 'JOB-CRM-9899', name: 'Event lanyards — MediaGroup', client: 'MediaGroup UK', receivedDate: '18/07/2026', deliveries: 2, pos: 2, sos: 2, totalPoValue: '£4,400', totalPoRaw: 4400, totalSoValue: '£5,280', totalSoRaw: 5280, status: 'complete', description: 'Full-colour dye-sublimation lanyards with safety breakaway for MediaGroup annual conference.' },
  { id: 'job-crm-9891', crmRef: 'JOB-CRM-9891', name: 'Promo pens — LegalFirm', client: 'Hartley & Stone LLP', receivedDate: '10/07/2026', deliveries: 1, pos: 1, sos: 1, totalPoValue: '£2,100', totalPoRaw: 2100, totalSoValue: '£2,520', totalSoRaw: 2520, status: 'cancelled', description: 'Engraved metal ballpoint pens — cancelled by client due to budget freeze.' },
  { id: 'job-crm-9887', crmRef: 'JOB-CRM-9887', name: 'Tees and hoodies — StartupFest', client: 'StartupFest Ltd', receivedDate: '05/07/2026', deliveries: 4, pos: 4, sos: 4, totalPoValue: '£28,400', totalPoRaw: 28400, totalSoValue: '£34,080', totalSoRaw: 34080, status: 'complete', description: 'Screen-printed event tees and zip hoodies for StartupFest 2026 speakers and staff.' },
  { id: 'job-crm-9881', crmRef: 'JOB-CRM-9881', name: 'Umbrellas — InsuranceCo', client: 'InsuranceCo Group', receivedDate: '01/07/2026', deliveries: 2, pos: 2, sos: 2, totalPoValue: '£11,600', totalPoRaw: 11600, totalSoValue: '£13,920', totalSoRaw: 13920, status: 'complete', description: 'Golf umbrellas with full-panel branded print for InsuranceCo client gifts.' },
];

const posForJob: Record<string, { id: string; ref: string; supplier: string; total: string; totalRaw: number; status: string; deliveryRef: string; createdDate: string; linkedSo: string }[]> = {
  'job-crm-9924': [
    { id: 'po-2851', ref: 'PO-2851', supplier: 'Gildan Activewear EU', total: '£12,400', totalRaw: 12400, status: 'acknowledged', deliveryRef: 'DEL-9924-01', createdDate: '15/08/2026', linkedSo: 'SO-3241' },
    { id: 'po-2852', ref: 'PO-2852', supplier: 'EcoPromo Supplies', total: '£8,200', totalRaw: 8200, status: 'sent', deliveryRef: 'DEL-9924-02', createdDate: '15/08/2026', linkedSo: 'SO-3242' },
    { id: 'po-2853', ref: 'PO-2853', supplier: 'PrintFast UK', total: '£6,800', totalRaw: 6800, status: 'received', deliveryRef: 'DEL-9924-03', createdDate: '15/08/2026', linkedSo: 'SO-3243' },
    { id: 'po-2854', ref: 'PO-2854', supplier: 'HydroFlask Trade', total: '£11,000', totalRaw: 11000, status: 'draft', deliveryRef: 'DEL-9924-04', createdDate: '16/08/2026', linkedSo: 'SO-3244' },
  ],
  'job-crm-9921': [
    { id: 'po-2847', ref: 'PO-2847', supplier: 'Gildan Activewear EU', total: '£13,200', totalRaw: 13200, status: 'acknowledged', deliveryRef: 'DEL-9921-01', createdDate: '13/08/2026', linkedSo: 'SO-3238' },
    { id: 'po-2848', ref: 'PO-2848', supplier: 'Gildan Activewear EU', total: '£9,600', totalRaw: 9600, status: 'sent', deliveryRef: 'DEL-9921-02', createdDate: '13/08/2026', linkedSo: 'SO-3239' },
  ],
  'job-crm-9919': [
    { id: 'po-2843', ref: 'PO-2843', supplier: 'Moleskine Trade', total: '£9,400', totalRaw: 9400, status: 'acknowledged', deliveryRef: 'DEL-9919-01', createdDate: '11/08/2026', linkedSo: 'SO-3231' },
    { id: 'po-2842', ref: 'PO-2842', supplier: 'PrintFast UK', total: '£7,200', totalRaw: 7200, status: 'sent', deliveryRef: 'DEL-9919-02', createdDate: '11/08/2026', linkedSo: 'SO-3232' },
  ],
  'job-crm-9917': [
    { id: 'po-2838', ref: 'PO-2838', supplier: 'EcoPromo Supplies', total: '£4,800', totalRaw: 4800, status: 'received', deliveryRef: 'DEL-9917-01', createdDate: '08/08/2026', linkedSo: 'SO-3225' },
  ],
  'job-crm-9915': [
    { id: 'po-2835', ref: 'PO-2835', supplier: 'Gildan Activewear EU', total: '£14,200', totalRaw: 14200, status: 'closed', deliveryRef: 'DEL-9915-01', createdDate: '05/08/2026', linkedSo: 'SO-3218' },
  ],
  'job-crm-9912': [
    { id: 'po-2831', ref: 'PO-2831', supplier: 'Headwear Direct', total: '£7,800', totalRaw: 7800, status: 'closed', deliveryRef: 'DEL-9912-01', createdDate: '01/08/2026', linkedSo: 'SO-3211' },
  ],
  'job-crm-9908': [
    { id: 'po-2821', ref: 'PO-2821', supplier: 'OfficeWorks Trade', total: '£5,400', totalRaw: 5400, status: 'closed', deliveryRef: 'DEL-9908-01', createdDate: '28/07/2026', linkedSo: 'SO-3201' },
    { id: 'po-2822', ref: 'PO-2822', supplier: 'DeskPro Supplies', total: '£4,400', totalRaw: 4400, status: 'closed', deliveryRef: 'DEL-9908-02', createdDate: '28/07/2026', linkedSo: 'SO-3202' },
  ],
};

const sosForJob: Record<string, { id: string; ref: string; client: string; total: string; totalRaw: number; status: string; linkedPo: string; createdDate: string; invoiceNumber: string; invoiceStatus: string }[]> = {
  'job-crm-9924': [
    { id: 'so-3241', ref: 'SO-3241', client: 'TechConf Events Ltd', total: '£14,880', totalRaw: 14880, status: 'confirmed', linkedPo: 'PO-2851', createdDate: '15/08/2026', invoiceNumber: '—', invoiceStatus: '—' },
    { id: 'so-3242', ref: 'SO-3242', client: 'TechConf Events Ltd', total: '£9,840', totalRaw: 9840, status: 'confirmed', linkedPo: 'PO-2852', createdDate: '15/08/2026', invoiceNumber: '—', invoiceStatus: '—' },
    { id: 'so-3243', ref: 'SO-3243', client: 'TechConf Events Ltd', total: '£8,160', totalRaw: 8160, status: 'fulfilled', linkedPo: 'PO-2853', createdDate: '15/08/2026', invoiceNumber: 'INV-10479', invoiceStatus: 'overdue' },
    { id: 'so-3244', ref: 'SO-3244', client: 'TechConf Events Ltd', total: '£13,200', totalRaw: 13200, status: 'draft', linkedPo: 'PO-2854', createdDate: '16/08/2026', invoiceNumber: '—', invoiceStatus: '—' },
  ],
  'job-crm-9921': [
    { id: 'so-3238', ref: 'SO-3238', client: 'RetailCo UK', total: '£15,840', totalRaw: 15840, status: 'invoiced', linkedPo: 'PO-2847', createdDate: '13/08/2026', invoiceNumber: 'INV-10481', invoiceStatus: 'due' },
    { id: 'so-3239', ref: 'SO-3239', client: 'RetailCo UK', total: '£11,520', totalRaw: 11520, status: 'confirmed', linkedPo: 'PO-2848', createdDate: '13/08/2026', invoiceNumber: 'INV-10477', invoiceStatus: 'overdue' },
  ],
  'job-crm-9919': [
    { id: 'so-3231', ref: 'SO-3231', client: 'SaaS Summit Ltd', total: '£11,280', totalRaw: 11280, status: 'confirmed', linkedPo: 'PO-2843', createdDate: '11/08/2026', invoiceNumber: 'INV-10473', invoiceStatus: 'sent' },
    { id: 'so-3232', ref: 'SO-3232', client: 'SaaS Summit Ltd', total: '£8,640', totalRaw: 8640, status: 'confirmed', linkedPo: 'PO-2842', createdDate: '11/08/2026', invoiceNumber: '—', invoiceStatus: '—' },
  ],
  'job-crm-9917': [
    { id: 'so-3225', ref: 'SO-3225', client: 'GreenFest CIC', total: '£5,760', totalRaw: 5760, status: 'fulfilled', linkedPo: 'PO-2838', createdDate: '08/08/2026', invoiceNumber: 'INV-10480', invoiceStatus: 'paid' },
  ],
  'job-crm-9915': [
    { id: 'so-3218', ref: 'SO-3218', client: 'HealthOrg NHS', total: '£37,200', totalRaw: 37200, status: 'invoiced', linkedPo: 'PO-2835', createdDate: '05/08/2026', invoiceNumber: 'INV-10478', invoiceStatus: 'sent' },
  ],
  'job-crm-9912': [
    { id: 'so-3211', ref: 'SO-3211', client: 'SportsBrand Ltd', total: '£21,840', totalRaw: 21840, status: 'invoiced', linkedPo: 'PO-2831', createdDate: '01/08/2026', invoiceNumber: 'INV-10476', invoiceStatus: 'overdue' },
  ],
  'job-crm-9908': [
    { id: 'so-3201', ref: 'SO-3201', client: 'FinanceCorp PLC', total: '£6,480', totalRaw: 6480, status: 'invoiced', linkedPo: 'PO-2821', createdDate: '28/07/2026', invoiceNumber: 'INV-10461', invoiceStatus: 'paid' },
    { id: 'so-3202', ref: 'SO-3202', client: 'FinanceCorp PLC', total: '£5,280', totalRaw: 5280, status: 'invoiced', linkedPo: 'PO-2822', createdDate: '28/07/2026', invoiceNumber: 'INV-10462', invoiceStatus: 'paid' },
  ],
};

// ─── Delivery address per PO (1 per PO) ──────────────────────────────────────

interface DeliveryAddress {
  poId: string;
  poRef: string;
  label: string;
  recipient: string;
  line1: string;
  line2?: string;
  city: string;
  postcode: string;
  country: string;
  notes?: string;
}

const deliveryAddressByPo: Record<string, DeliveryAddress> = {
  'po-2854': { poId: 'po-2854', poRef: 'PO-2854', label: 'Main venue', recipient: 'TechConf Events Ltd — Goods In', line1: 'ExCeL London, One Western Gateway', city: 'London', postcode: 'E16 1XL', country: 'UK', notes: 'Deliver to loading bay B, Mon–Fri 08:00–16:00' },
  'po-2853': { poId: 'po-2853', poRef: 'PO-2853', label: 'Conference centre', recipient: 'TechConf Events Ltd — Receiving', line1: 'ExCeL London, One Western Gateway', city: 'London', postcode: 'E16 1XL', country: 'UK', notes: 'Attn: Logistics Manager — ref PO-2853' },
  'po-2852': { poId: 'po-2852', poRef: 'PO-2852', label: 'Primary delivery', recipient: 'TechConf Events Ltd', line1: 'ExCeL London, One Western Gateway', city: 'London', postcode: 'E16 1XL', country: 'UK' },
  'po-2851': { poId: 'po-2851', poRef: 'PO-2851', label: 'Main venue', recipient: 'TechConf Events Ltd — Goods In', line1: 'ExCeL London, One Western Gateway', city: 'London', postcode: 'E16 1XL', country: 'UK', notes: 'Deliver to loading bay B' },
  'po-2848': { poId: 'po-2848', poRef: 'PO-2848', label: 'Head office', recipient: 'RetailCo UK — Merchandise Team', line1: '200 Deansgate', city: 'Manchester', postcode: 'M3 3WB', country: 'UK', notes: 'Contact Sarah Booth on arrival: 07700 900 123' },
  'po-2847': { poId: 'po-2847', poRef: 'PO-2847', label: 'Distribution centre', recipient: 'RetailCo UK — DC Inbound', line1: 'Unit 12, Trafford Park Industrial Estate', line2: 'Trafford Park', city: 'Manchester', postcode: 'M17 1PQ', country: 'UK', notes: 'Book delivery slot 48 hrs in advance' },
  'po-2843': { poId: 'po-2843', poRef: 'PO-2843', label: 'Venue goods in', recipient: 'SaaS Summit Ltd', line1: 'The Brewery, 52 Chiswell Street', city: 'London', postcode: 'EC1Y 4SD', country: 'UK' },
  'po-2842': { poId: 'po-2842', poRef: 'PO-2842', label: 'Summit venue', recipient: 'SaaS Summit Ltd — Events Team', line1: 'The Brewery, 52 Chiswell Street', city: 'London', postcode: 'EC1Y 4SD', country: 'UK', notes: 'Deliver day before event — 18 Aug 2026' },
  'po-2838': { poId: 'po-2838', poRef: 'PO-2838', label: 'Festival site', recipient: 'GreenFest CIC — Volunteer Coordinator', line1: 'Victoria Park', line2: 'Grove Road', city: 'London', postcode: 'E3 5TB', country: 'UK', notes: 'Eco-friendly packaging required' },
  'po-2835': { poId: 'po-2835', poRef: 'PO-2835', label: 'NHS Trust HQ', recipient: 'HealthOrg NHS — Procurement', line1: 'Trust HQ, 330 Grays Inn Road', city: 'London', postcode: 'WC1X 8DA', country: 'UK', notes: 'NHS delivery compliance required — ref NHS-PO-2835' },
  'po-2831': { poId: 'po-2831', poRef: 'PO-2831', label: 'Warehouse', recipient: 'SportsBrand Ltd — Inbound Logistics', line1: 'Unit 7, Magna Park', line2: 'Lutterworth', city: 'Lutterworth', postcode: 'LE17 4XN', country: 'UK', notes: 'Pallet delivery only — contact warehouse before dispatch' },
  'po-2821': { poId: 'po-2821', poRef: 'PO-2821', label: 'Head office', recipient: 'FinanceCorp PLC — Office Manager', line1: '1 Canada Square', city: 'London', postcode: 'E14 5AB', country: 'UK', notes: 'Deliver to reception, Mon–Fri 09:00–17:00' },
  'po-2822': { poId: 'po-2822', poRef: 'PO-2822', label: 'Regional office', recipient: 'FinanceCorp PLC — Manchester', line1: '3 Hardman Street', city: 'Manchester', postcode: 'M3 3HF', country: 'UK' },
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function JobLandingContent({ jobId }: { jobId: string }) {
  const [activeTab, setActiveTab] = useState<'pos' | 'sos'>('pos');

  const job = jobsData.find(j => j.id === jobId);
  const pos = posForJob[jobId] ?? [];
  const sos = sosForJob[jobId] ?? [];

  if (!job) {
    return (
      <div className="flex flex-col items-center justify-center py-24 space-y-4">
        <Briefcase size={40} style={{ color: 'var(--border)' }} />
        <p className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>Job not found</p>
        <p className="text-sm" style={{ color: 'var(--slate)' }}>No job with ID &quot;{jobId}&quot; exists.</p>
        <Link href="/jobs" className="flex items-center gap-2 text-sm font-medium" style={{ color: 'var(--accent)' }}>
          <ArrowLeft size={14} /> Back to Jobs
        </Link>
      </div>
    );
  }

  // Margin KPI
  const totalPoRaw = pos.reduce((s, p) => s + p.totalRaw, 0);
  const totalSoRaw = sos.reduce((s, s2) => s + s2.totalRaw, 0);

  // Use fixed values for job-crm-9908 (Desk kits — FinanceCorp): PO=9800, SO=11760
  const effectivePoRaw = jobId === 'job-crm-9908' ? 9800 : (totalPoRaw > 0 ? totalPoRaw : job.totalPoRaw);
  const effectiveSoRaw = jobId === 'job-crm-9908' ? 11760 : (totalSoRaw > 0 ? totalSoRaw : job.totalSoRaw);

  const marginAmount = effectiveSoRaw - effectivePoRaw;
  const marginPct = effectiveSoRaw > 0 ? (marginAmount / effectiveSoRaw) * 100 : 0;
  const isPositiveMargin = marginAmount >= 0;

  const fmt = (n: number) => `£${Math.abs(n).toLocaleString('en-GB')}`;

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--slate)' }}>
        <Link href="/jobs" className="hover:underline" style={{ color: 'var(--accent)' }}>Jobs</Link>
        <ChevronRight size={14} />
        <span style={{ color: 'var(--foreground)' }}>{job.crmRef}</span>
      </div>

      {/* Job header */}
      <div
        className="rounded-xl border p-6"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <span className="text-xs font-mono font-semibold px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--muted)', color: 'var(--slate)' }}>
                {job.crmRef}
              </span>
              <StatusBadge status={job.status as never} />
            </div>
            <h1 className="text-2xl font-semibold mt-2" style={{ color: 'var(--foreground)', letterSpacing: '-0.01em' }}>
              {job.name}
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--slate)' }}>{job.description}</p>
          </div>
          <Link
            href="/jobs"
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border transition-colors hover:bg-muted"
            style={{ borderColor: 'var(--border)', color: 'var(--slate)' }}
          >
            <ArrowLeft size={14} />
            Back to Jobs
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 pt-5 border-t" style={{ borderColor: 'var(--border)' }}>
          {[
            { label: 'Client', value: job.client },
            { label: 'Received', value: job.receivedDate },
            { label: 'Deliveries', value: String(job.deliveries) },
            { label: 'POs / SOs', value: `${job.pos} / ${job.sos}` },
          ].map(f => (
            <div key={`jf-${f.label}`}>
              <p className="text-xs font-medium" style={{ color: 'var(--slate)' }}>{f.label}</p>
              <p className="text-sm font-semibold mt-0.5" style={{ color: 'var(--foreground)' }}>{f.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {/* Total SO value */}
        <div
          className="rounded-xl border p-4"
          style={{ backgroundColor: 'var(--teal-light)', borderColor: 'var(--accent)' }}
        >
          <p className="text-xs font-medium mb-1" style={{ color: 'var(--accent)' }}>Total SO value</p>
          <p className="text-2xl font-bold tabular-nums" style={{ color: 'var(--accent)' }}>
            {fmt(effectiveSoRaw)}
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--accent)', opacity: 0.7 }}>{sos.length} sales orders</p>
        </div>

        {/* Total PO value */}
        <div
          className="rounded-xl border p-4"
          style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
        >
          <p className="text-xs font-medium mb-1" style={{ color: 'var(--slate)' }}>Total PO value</p>
          <p className="text-2xl font-bold tabular-nums" style={{ color: 'var(--foreground)' }}>
            {fmt(effectivePoRaw)}
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--slate)' }}>{pos.length} purchase orders</p>
        </div>

        {/* Margin £ */}
        <div
          className="rounded-xl border p-4"
          style={{
            backgroundColor: isPositiveMargin ? 'var(--teal-light)' : '#FEF2F2',
            borderColor: isPositiveMargin ? 'var(--accent)' : '#FCA5A5',
          }}
        >
          <div className="flex items-center gap-1.5 mb-1">
            {isPositiveMargin
              ? <TrendingUp size={13} style={{ color: 'var(--accent)' }} />
              : <TrendingDown size={13} style={{ color: '#DC2626' }} />
            }
            <p className="text-xs font-medium" style={{ color: isPositiveMargin ? 'var(--accent)' : '#DC2626' }}>
              Margin (SO − PO)
            </p>
          </div>
          <p className="text-2xl font-bold tabular-nums" style={{ color: isPositiveMargin ? 'var(--accent)' : '#DC2626' }}>
            {isPositiveMargin ? '' : '−'}{fmt(marginAmount)}
          </p>
          <p className="text-xs mt-1" style={{ color: isPositiveMargin ? 'var(--accent)' : '#DC2626', opacity: 0.7 }}>
            gross margin amount
          </p>
        </div>

        {/* Margin % */}
        <div
          className="rounded-xl border p-4"
          style={{
            backgroundColor: isPositiveMargin ? 'var(--card)' : '#FEF2F2',
            borderColor: isPositiveMargin ? 'var(--border)' : '#FCA5A5',
          }}
        >
          <div className="flex items-center gap-1.5 mb-1">
            {isPositiveMargin
              ? <TrendingUp size={13} style={{ color: 'var(--accent)' }} />
              : <TrendingDown size={13} style={{ color: '#DC2626' }} />
            }
            <p className="text-xs font-medium" style={{ color: isPositiveMargin ? 'var(--slate)' : '#DC2626' }}>
              Margin %
            </p>
          </div>
          <p className="text-2xl font-bold tabular-nums" style={{ color: isPositiveMargin ? 'var(--foreground)' : '#DC2626' }}>
            {isPositiveMargin ? '' : '−'}{Math.abs(marginPct).toFixed(1)}%
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--slate)' }}>
            (SO − PO) / SO
          </p>
        </div>
      </div>

      {/* Tabs: POs / SOs */}
      <div
        className="rounded-xl border overflow-hidden"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        {/* Tab bar */}
        <div className="flex border-b" style={{ borderColor: 'var(--border)' }}>
          {[
            { key: 'pos' as const, label: 'Purchase Orders', icon: ShoppingCart, count: pos.length },
            { key: 'sos' as const, label: 'Sales Orders', icon: FileText, count: sos.length },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors"
              style={{
                borderBottomColor: activeTab === tab.key ? 'var(--accent)' : 'transparent',
                color: activeTab === tab.key ? 'var(--accent)' : 'var(--slate)',
                backgroundColor: 'transparent',
              }}
            >
              <tab.icon size={15} />
              {tab.label}
              <span
                className="text-xs font-semibold px-1.5 py-0.5 rounded-full"
                style={{
                  backgroundColor: activeTab === tab.key ? 'var(--teal-light)' : 'var(--muted)',
                  color: activeTab === tab.key ? 'var(--accent)' : 'var(--slate)',
                }}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* POs table */}
        {activeTab === 'pos' && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', backgroundColor: 'var(--cloud)' }}>
                  {['PO ref', 'Delivery ref', 'Supplier', 'Created', 'Total', 'Linked SO', 'Status', ''].map((h, i) => (
                    <th
                      key={`po-th-${i}`}
                      className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap"
                      style={{ color: 'var(--slate)', letterSpacing: '0.05em' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pos.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12">
                      <ShoppingCart size={28} className="mx-auto mb-2" style={{ color: 'var(--border)' }} />
                      <p className="text-sm" style={{ color: 'var(--slate)' }}>No purchase orders for this job yet</p>
                    </td>
                  </tr>
                ) : (
                  pos.map(po => (
                    <tr
                      key={po.id}
                      className="border-b last:border-0"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      <td className="px-4 py-3">
                        <Link
                          href={`/purchase-orders/${po.id}`}
                          className="text-xs font-mono font-semibold hover:underline"
                          style={{ color: 'var(--accent)' }}
                        >
                          {po.ref}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono" style={{ color: 'var(--slate)' }}>{po.deliveryRef}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span style={{ color: 'var(--foreground)' }}>{po.supplier}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span style={{ color: 'var(--slate)' }}>{po.createdDate}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="tabular-nums font-semibold" style={{ color: 'var(--foreground)' }}>{po.total}</span>
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          href={`/sales-orders/${po.linkedSo.toLowerCase().replace('-', '-')}`}
                          className="text-xs font-mono hover:underline"
                          style={{ color: 'var(--accent)' }}
                        >
                          {po.linkedSo}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={po.status as never} />
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          href={`/purchase-orders/${po.id}`}
                          className="flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md border transition-colors hover:bg-muted"
                          style={{ borderColor: 'var(--border)', color: 'var(--slate)' }}
                        >
                          Open <ChevronRight size={12} />
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* SOs table */}
        {activeTab === 'sos' && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', backgroundColor: 'var(--cloud)' }}>
                  {['SO ref', 'Client', 'Created', 'Total', 'Linked PO', 'Status', 'Invoice', ''].map((h, i) => (
                    <th
                      key={`so-th-${i}`}
                      className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap"
                      style={{ color: 'var(--slate)', letterSpacing: '0.05em' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sos.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12">
                      <FileText size={28} className="mx-auto mb-2" style={{ color: 'var(--border)' }} />
                      <p className="text-sm" style={{ color: 'var(--slate)' }}>No sales orders for this job yet</p>
                    </td>
                  </tr>
                ) : (
                  sos.map(so => (
                    <tr
                      key={so.id}
                      className="border-b last:border-0"
                      style={{
                        borderColor: 'var(--border)',
                        backgroundColor: so.invoiceStatus === 'overdue' ? 'var(--amber-light)' : undefined,
                      }}
                    >
                      <td className="px-4 py-3">
                        <Link
                          href={`/sales-orders/${so.id}`}
                          className="text-xs font-mono font-semibold hover:underline"
                          style={{ color: 'var(--accent)' }}
                        >
                          {so.ref}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <span style={{ color: 'var(--foreground)' }}>{so.client}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span style={{ color: 'var(--slate)' }}>{so.createdDate}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="tabular-nums font-semibold" style={{ color: 'var(--accent)' }}>{so.total}</span>
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          href={`/purchase-orders/${so.linkedPo.toLowerCase().replace('po-', 'po-')}`}
                          className="text-xs font-mono hover:underline"
                          style={{ color: 'var(--slate)' }}
                        >
                          {so.linkedPo}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={so.status as never} />
                      </td>
                      <td className="px-4 py-3">
                        {so.invoiceNumber !== '—' ? (
                          <div className="flex items-center gap-2">
                            <StatusBadge status={so.invoiceStatus as never} pulse={so.invoiceStatus === 'overdue'} />
                            <span className="text-xs font-mono" style={{ color: 'var(--slate)' }}>{so.invoiceNumber}</span>
                          </div>
                        ) : (
                          <span className="text-xs" style={{ color: 'var(--slate)' }}>No invoice</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          href={`/sales-orders/${so.id}`}
                          className="flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md border transition-colors hover:bg-muted"
                          style={{ borderColor: 'var(--border)', color: 'var(--slate)' }}
                        >
                          Open <ChevronRight size={12} />
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Deliveries summary */}
      <div
        className="rounded-xl border overflow-hidden"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: 'var(--border)' }}>
          <MapPin size={15} style={{ color: 'var(--accent)' }} />
          <h3 className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Delivery addresses</h3>
          <span className="text-xs font-medium px-2 py-0.5 rounded-full ml-auto" style={{ backgroundColor: 'var(--teal-light)', color: 'var(--accent)' }}>
            {pos.filter(p => deliveryAddressByPo[p.id]).length} address{pos.filter(p => deliveryAddressByPo[p.id]).length !== 1 ? 'es' : ''}
          </span>
        </div>
        {pos.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <MapPin size={28} className="mx-auto mb-2" style={{ color: 'var(--border)' }} />
            <p className="text-sm" style={{ color: 'var(--slate)' }}>No purchase orders — no delivery addresses yet</p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
            {pos.map(po => {
              const addr = deliveryAddressByPo[po.id];
              return (
                <div key={`da-${po.id}`} className="px-5 py-4 flex items-start gap-4">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: 'var(--teal-light)', color: 'var(--accent)' }}
                  >
                    <MapPin size={15} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Link
                        href={`/purchase-orders/${po.id}`}
                        className="text-xs font-mono font-semibold hover:underline"
                        style={{ color: 'var(--accent)' }}
                      >
                        {po.ref}
                      </Link>
                      {addr && (
                        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--slate)' }}>
                          {addr.label}
                        </span>
                      )}
                    </div>
                    {addr ? (
                      <>
                        <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{addr.recipient}</p>
                        <p className="text-sm mt-0.5" style={{ color: 'var(--slate)' }}>
                          {addr.line1}{addr.line2 ? `, ${addr.line2}` : ''}, {addr.city}, {addr.postcode}, {addr.country}
                        </p>
                        {addr.notes && (
                          <p className="text-xs mt-1.5 px-2.5 py-1.5 rounded-md" style={{ backgroundColor: 'var(--cloud)', color: 'var(--slate)' }}>
                            📋 {addr.notes}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm" style={{ color: 'var(--slate)' }}>No delivery address recorded</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
